<template>
  <div class="control-page" ref="animationContainer">
    <div class="header-row">
      <button @click="goBack" class="back-btn">← Back to Control List</button>
      <h1>Control Room</h1>
    </div>

    <div v-if="loading">Loading countdown…</div>
    <div v-else-if="!sessionId" class="no-session">
      <div class="error-message">
        <h2>❌ No Countdown Found</h2>
        <p>Invalid or expired control link.</p>
        <button @click="goBack" class="link-btn">Go to Control List</button>
      </div>
    </div>
    <div v-else>
      <!-- Warning when controlling yourself -->
      <div v-if="isControllingMyself" class="self-control-warning">
        <h3>⚠️ You're viewing your own countdown</h3>
        <p>
          You can't control yourself from here. Go to the
          <router-link to="/countdown">Countdown page</router-link> to view your own countdown.
        </p>
      </div>

      <!-- Session owner info -->
      <div v-if="owner" class="session-owner">
        <mini-user
          :online="owner.online"
          :fullname="owner.name"
          :photo_url="owner.photo_url"
          :username="owner.username"
          :user_id="owner.id"
        />
      </div>

      <!-- Join & anonymity panel (for non-owners) -->
      <section
        v-if="!isControllingMyself"
        class="join-panel"
        :class="{ collapsed: joined && joinPanelCollapsed }"
      >
        <h2 @click="joinPanelCollapsed = !joinPanelCollapsed">
          <span class="triangle" :class="{ open: contentExpanded }" aria-hidden="true"></span>
          <span>{{ joined ? 'Anonymity Settings' : 'Join as Controller' }}</span>
        </h2>
        <p class="muted" v-if="!joined && contentExpanded">
          Join this countdown to control it. Choose how you appear to others.
        </p>

        <!-- Transition-wrapped content for animated collapse/expand -->
        <transition name="join-content" appear @enter="onEnter" @leave="onLeave">
          <div v-if="contentExpanded" class="join-content" @click.stop>
            <anon-settings v-model="anon" whose="my" />
            <div class="presence-toggle">
              <input type="checkbox" id="hide-online" v-model="anon.hide_online_status" />
              <label for="hide-online">Hide my online status</label>
            </div>
            <div class="actions">
              <button v-if="!joined" class="link-btn" :disabled="joining" @click="join()">
                {{ joining ? 'Joining…' : 'Join as controller' }}
              </button>
              <template v-else>
                <button class="link-btn" :disabled="saving" @click="saveAnon()">
                  {{ saving ? 'Saving…' : 'Save anonymity' }}
                </button>
                <button class="link-btn danger" :disabled="leaving" @click="leave()">
                  {{ leaving ? 'Leaving…' : 'Leave session' }}
                </button>
              </template>
              <span v-if="saved" class="saved">Saved!</span>
            </div>
          </div>
        </transition>
      </section>

      <div v-if="!state" class="no-state">
        <div class="error-message">
          <h2>❌ No Session State</h2>
          <p>Unable to retrieve countdown state. Please ensure you have joined the session.</p>
        </div>
      </div>
      <div v-else>
        <div class="status-section">
          <span class="label">Status:</span>
          <status-badge :status="state.status" />
        </div>

        <div v-if="state.status === 'created'" class="status-hint created">
          Owner has not opened this countdown yet. Waiting for them to be ready…
        </div>
        <div v-if="state.status === 'closed'" class="status-hint closed">
          Countdown closed. No further actions possible.
        </div>
        <div v-if="state.status === 'ready'" class="status-hint ready">
          Countdown open. Start it when they're desperate enough.
        </div>
        <div v-if="state.status === 'failsafe'" class="status-hint failsafe">
          Failsafe mode activated! Will start at a random time…
        </div>

        <div
          v-if="state.status === 'ready' || state.status === 'failsafe'"
          class="status-hint failsafe-info"
        >
          Failsafe mode is
          <span v-if="state.max_wait_left === null && state.random_wait_left === null">
            not configured, you have to start the countdown eventually.
          </span>
          <span v-else-if="state.max_wait_left === null">
            active, so the system will start the countdown at a random time within the next
            <computed-time :seconds="state.random_wait_left ?? 0" />
          </span>
          <span v-else-if="state.random_wait_left === null">
            configured to start the countdown in
            <computed-time :seconds="state.max_wait_left ?? 0" />
          </span>
          <span v-else>
            configured to start the countdown randomly between
            <computed-time :seconds="state.max_wait_left ?? 0" /> and
            <computed-time :seconds="state.random_wait_left ?? 0" />
          </span>
          <span v-if="state.max_wait_left !== null || state.random_wait_left !== null">
            automatically if you don't start it manually before then.
          </span>
        </div>

        <div v-if="state.status === 'countdown' || state.status === 'paused'">
          <div class="countdown" ref="countdownDisplay">
            Time left: {{ formatDuration(state.time_left ?? state.countdown_seconds) }}
          </div>
        </div>
        <div v-else-if="state.status === 'ready' || state.status === 'created'">
          <div class="countdown placeholder">
            Countdown length: {{ formatDuration(state.countdown_seconds) }}
          </div>
        </div>

        <div v-if="state.status === 'success'">
          <div class="success">✅ Success! They came in time.</div>
        </div>

        <div v-if="state.status === 'fail'">
          <div class="fail" :class="failReasonClass">
            <template v-if="isFailTimeout">❌ Time's up!</template>
            <template v-else>🚫 They came when not allowed!</template>
            Enter punishment:
          </div>
        </div>

        <div v-if="state.status === 'fail' && !isControllingMyself">
          <input v-model="punishment" placeholder="Type punishment..." />
          <button
            ref="punishmentButton"
            @click="submitPunishment"
            :disabled="!punishment"
            class="punishment-btn"
          >
            Submit
          </button>
        </div>
        <div v-else-if="state.status === 'fail' && isControllingMyself">
          <div class="fail" :class="failReasonClass">
            <template v-if="isFailTimeout"
              >❌ Time's up! Check your punishment on the countdown page.</template
            >
            <template v-else
              >🚫 Self‑fail recorded! Check punishment on the countdown page.</template
            >
          </div>
        </div>

        <!-- Show submitted punishment (if any) -->
        <div v-if="state.status === 'fail' && state.punishment_text" class="punishment-box">
          <div class="punishment-title">Punishment</div>
          <div class="punishment-text">{{ state.punishment_text }}</div>
        </div>

        <!-- Control buttons - hidden when controlling yourself -->
        <div v-if="!isControllingMyself" class="actions">
          <button @click="startCountdown" :disabled="!joined || !can('start')">
            {{ state.status === 'paused' ? 'Resume' : 'Start' }}
          </button>
          <button @click="pauseCountdown" :disabled="!joined || !can('pause')">Pause</button>
          <button @click="restartCountdown" :disabled="!joined || !can('restart')">Restart</button>
          <button @click="resetCountdown" :disabled="!joined || !can('reset')">Reset</button>
        </div>

        <section class="controllers">
          <h2>Connected Users</h2>

          <!-- Countdown Owner Online Status -->
          <div class="owner-status">
            <div class="avatar owner">
              <mini-user
                v-if="owner"
                :online="owner?.online"
                :fullname="owner?.name || 'Anonymous'"
                :photo_url="owner?.photo_url"
                :user_id="owner?.id"
              />
            </div>
          </div>

          <!-- Controllers -->
          <div class="section-group">
            <h3>Controllers ({{ controllers.length }})</h3>
            <div class="avatars" v-if="controllers.length">
              <div v-for="c in controllers" :key="`controller-${c.id}`" class="avatar controller">
                <mini-user
                  :online="c?.hide_online_status ? null : (c?.online ?? undefined)"
                  :fullname="c.name"
                  :photo_url="c.photo_url"
                  :user_id="c.id"
                />
              </div>
            </div>
            <p v-else class="muted">No controllers connected right now.</p>
          </div>
        </section>

        <p v-if="error" class="error">{{ error }}</p>

        <section class="debug-panel">
          <button class="link-btn" @click="showDebug = !showDebug">
            {{ showDebug ? 'Hide' : 'Show' }} WS Debug
          </button>
          <div v-if="showDebug" class="log">
            <pre v-for="(line, idx) in ws.debugLog" :key="idx">{{ line }}</pre>
          </div>
          <debug-widget :data="state" name="state" :open="false" />
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '@/api'
import { useAuthStore } from '@/stores/auth'
import {
  canInSession,
  type Command,
  type SessionID,
  type SessionStateOut,
  type SessionUUID,
  useWsStore,
} from '@/stores/ws'
import { useToastStore } from '@/stores/toast'
import { useSessionActivity } from '@/composables/useSessionActivity'
import { useNotifications } from '@/composables/useNotifications'
import MiniUser from '@/components/MiniUser.vue'
import { formatDuration } from '@/utils/date'
import StatusBadge from '@/components/StatusBadge.vue'
import AnonSettings from '@/components/AnonSettings.vue'
import type { UserOut } from '@/schemas'
import type { ControllerOut } from '@/schemas/schemas.ts'
import ComputedTime from '@/components/ComputedTime.vue'
import DebugWidget from '@/components/DebugWidget.vue'

interface Session {
  id: SessionID
  user_id: number
  user?: UserOut
  controller_default_anon_username?: boolean
  controller_default_anon_name?: boolean
  controller_default_anon_photo?: boolean
  [key: string]: unknown
}

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ws = useWsStore()
const toasts = useToastStore()
const { requestPermission, notifyEvent } = useNotifications()

const can = computed(() => (action: Command) => canInSession(state.value, 'controller', action))

const sessionId = ref<SessionID | null>(null)
const linkUuid = ref<SessionUUID | null>(null)
const currentSession = ref<Session | null>(null)
const loading = ref(true)
const punishment = ref('')
const error = ref<string | null>(null)
const controllers = ref<ControllerOut[]>([])
const showDebug = ref(false)

// Participant state
const joined = ref(false)
const joining = ref(false)
const saving = ref(false)
const leaving = ref(false)
const saved = ref(false)
const anon = ref({
  anon_username: false,
  anon_name: false,
  anon_photo: false,
  hide_online_status: false,
})
// Collapse behavior for join panel
const joinPanelCollapsed = ref(false)
const contentExpanded = computed(() => !joined.value || !joinPanelCollapsed.value)

const state = computed<SessionStateOut | null>(() =>
  sessionId.value ? ws.getState(sessionId.value) : null,
)
const owner = computed(() => {
  return state.value?.owner_connected ?? state.value?.owner
})

// Use window focus handling for automatic watch/unwatch
// Only establish WS after picking anonymity (i.e., after join)
const controllerReady = computed(() => !!sessionId.value && joined.value)
useSessionActivity(() => (controllerReady.value ? sessionId.value : null))

// Check if the current user is controlling their own session
const isControllingMyself = computed(() => {
  return currentSession.value && auth.user && currentSession.value?.user_id === auth.user.id
})

function goBack() {
  router.push('/control-list')
}

function sendIfCanControl(command: Command, payload?: Record<string, unknown>) {
  if (!sessionId.value) {
    toasts.add('No active session to control', 'error', `(command=${command})`)
    return
  }
  if (isControllingMyself.value) {
    toasts.add(
      `You cannot control your own session from here. Go to the Countdown page.`,
      'warning',
      `(command=${command}, sessionId=${sessionId.value})`,
    )
    return
  }
  if (!joined.value) {
    toasts.add('Join the session first to control it', 'info')
    return
  }
  ws.sendCmd(sessionId.value, command, payload as Record<string, unknown> | undefined)
}

function startCountdown() {
  sendIfCanControl('start')
}
function pauseCountdown() {
  sendIfCanControl('pause')
}
function restartCountdown() {
  sendIfCanControl('restart')
}
function resetCountdown() {
  sendIfCanControl('reset')
}
function submitPunishment() {
  if (!punishment.value) return
  sendIfCanControl('punish', { punishment_text: punishment.value })
  punishment.value = ''
}

async function loadParticipant() {
  if (!sessionId.value) return
  try {
    const resp = await apiFetch(`/api/sessions/${sessionId.value}/participants/me`)
    if (resp.ok) {
      const raw: unknown = await resp.json().catch(() => ({}))
      const p = typeof raw === 'object' && raw ? (raw as Record<string, unknown>) : {}
      anon.value.anon_username = p.anon_username === true
      anon.value.anon_name = p.anon_name === true
      anon.value.anon_photo = p.anon_photo === true
      anon.value.hide_online_status = p.hide_online_status === true
      joined.value = true
      // collapse the join panel when already participating
      joinPanelCollapsed.value = true
      startWebsocket()
      return
    } else if (resp.status === 404) {
      // Not joined yet
      toasts.add('You are not currently participating in this session', 'info')
      // all handled below anyway.
    } else {
      toasts.add(
        `Unknown error occurred when fetching session participation`,
        'warning',
        `Status: ${resp.status}\nBody: ${await resp.text()}`,
      )
    }
  } catch {
    toasts.add(`Could not fetch session participation.`, 'warning')
  }
  joined.value = false
  // show expanded join panel by default when not joined
  joinPanelCollapsed.value = false
  // Defaults from session
  if (currentSession.value) {
    anon.value.anon_username = !!currentSession.value.controller_default_anon_username
    anon.value.anon_name = !!currentSession.value.controller_default_anon_name
    anon.value.anon_photo = !!currentSession.value.controller_default_anon_photo
    anon.value.hide_online_status = false
  }
}
async function startWebsocket() {
  if (!sessionId.value) {
    console.log('Cannot start websocket without sessionId')
    return
  }
  // Force re-watch as controller to apply role immediately
  ws.startSession(sessionId.value, 'controller')
  ws.startActivityTracking(sessionId.value)
}

async function join() {
  if (!sessionId.value) return
  joining.value = true
  try {
    const body = JSON.stringify({
      ...anon.value,
      link_uuid: linkUuid.value || undefined,
    })
    const resp = await apiFetch(`/api/sessions/${sessionId.value}/participants/join`, {
      method: 'POST',
      body,
    })
    if (resp.ok) {
      joined.value = true
      toasts.add('Joined as controller', 'success')
      joinPanelCollapsed.value = true
      startWebsocket()
    } else {
      const errPayload: unknown = await resp.json().catch(() => ({}))
      let details = ''
      if (
        typeof errPayload === 'object' &&
        errPayload &&
        'detail' in (errPayload as Record<string, unknown>)
      ) {
        const d = (errPayload as Record<string, unknown>).detail
        if (Array.isArray(d)) {
          details = d
            .map((x) =>
              typeof x === 'object' && x && 'msg' in (x as Record<string, unknown>)
                ? String((x as Record<string, unknown>).msg)
                : JSON.stringify(x),
            )
            .join('; ')
        } else {
          details = d != null ? String(d) : ''
        }
      }
      toasts.add('Failed to join', 'error', details || null)
    }
  } finally {
    joining.value = false
  }
}

async function saveAnon() {
  if (!sessionId.value) return
  saving.value = true
  try {
    const resp = await apiFetch(`/api/sessions/${sessionId.value}/participants/me`, {
      method: 'PATCH',
      body: JSON.stringify(anon.value),
    })
    if (resp.ok) {
      saved.value = true
      setTimeout(() => {
        saved.value = false
        joinPanelCollapsed.value = true
      }, 1500)
      toasts.add('Anonymity updated', 'success')
      // collapse panel again after saving
    }
  } finally {
    saving.value = false
  }
}

async function leave() {
  if (!sessionId.value) return
  leaving.value = true
  try {
    const resp = await apiFetch(`/api/sessions/${sessionId.value}/participants/me`, {
      method: 'DELETE',
    })
    if (resp.ok) {
      joined.value = false
      // expand panel to allow re-joining and editing anonymity
      joinPanelCollapsed.value = false
      toasts.add('Left session', 'info')
      // Do not auto-connect as owner; keep socket disconnected until anon is chosen again
      ws.stopActivityTracking(sessionId.value)
    }
  } finally {
    leaving.value = false
  }
}

onMounted(async () => {
  // Ensure user is authenticated first
  if (!auth.user && auth.token) await auth.loadMe()

  if (!auth.user) {
    toasts.add('Please log in to access the control room', 'info')
    router.push('/login')
    return
  }
  requestPermission()

  // Handle both UUID links (?link=uuid) and legacy sessionId (?sessionId=123)
  const urlLinkUuid = route.query.link as SessionUUID
  const legacySessionId = route.query.sessionId
    ? (Number(route.query.sessionId) as SessionID)
    : null

  if (urlLinkUuid) {
    // New UUID-based link - get session from link and start watching
    try {
      const resp = await apiFetch(`/api/link/${urlLinkUuid}`)
      if (resp.ok) {
        const raw: unknown = await resp.json().catch(() => null)
        if (raw && typeof raw === 'object' && 'id' in (raw as Record<string, unknown>)) {
          const session = raw as unknown as Session
          sessionId.value = session.id
          linkUuid.value = urlLinkUuid
          currentSession.value = session
          await loadParticipant()
          loading.value = false
          return
        }
      }
    } catch (err) {
      console.error('Invalid control link:', err)
      error.value = 'Invalid control link'
      toasts.add('Invalid or expired control link', 'error')
      // show previous links
      router.push('/control-list')
    }
  } else if (legacySessionId) {
    // Legacy sessionId-based link - get a session and start watching
    try {
      const resp = await apiFetch(`/api/sessions/${legacySessionId}`)
      if (resp.ok) {
        const raw: unknown = await resp.json().catch(() => null)
        if (raw && typeof raw === 'object' && 'id' in (raw as Record<string, unknown>)) {
          const session = raw as unknown as Session
          sessionId.value = legacySessionId
          linkUuid.value = session.uuid as SessionUUID
          currentSession.value = session
          await loadParticipant()
          loading.value = false
          return
        }
      }
    } catch (err) {
      console.error('Legacy session error:', err)
      error.value = 'Session not found'
    }
  } else {
    // No session specified, redirect to control list
    router.push('/control-list')
    return
  }

  loading.value = false
})

// Toast on controller join/leave
let prevControllers = new Map<number, string>()

watch(
  () => state.value?.controllers ?? [],
  (curr) => {
    const currentMap = new Map<number, string>(curr.map((c) => [c.id, c.name || 'Anonymous']))
    // joins
    for (const [id, name] of currentMap) {
      if (!prevControllers.has(id)) {
        notifyEvent(`${name} joined as controller`, 'success', { verboseOnly: true })
      }
    }
    // leaves
    for (const [id, name] of prevControllers) {
      if (!currentMap.has(id)) {
        notifyEvent(`${name} left`, 'warning', { verboseOnly: true })
      }
    }
    prevControllers = currentMap
    controllers.value = curr
  },
)

// status change toasts enhancement (resume vs start)
watch(
  () => state.value?.status,
  (newStatus, oldStatus) => {
    if (newStatus === 'countdown' && oldStatus === 'paused') {
      notifyEvent('▶️ Countdown resumed!', 'info')
    } else if (newStatus === 'countdown' && oldStatus !== 'countdown' && oldStatus !== 'paused') {
      notifyEvent('⏰ Countdown started!', 'info', { body: 'Good luck!' })
    } else if (newStatus === 'success' && oldStatus !== 'success') {
      notifyEvent('✅ Success!', 'success', {
        body: 'Target succeeded',
        requireInteraction: true,
        autoClose: 0,
        noToast: true,
      })
    } else if (newStatus === 'aborted' && oldStatus !== 'aborted') {
      notifyEvent('🛑 Aborted', 'warning', { noToast: true })
    }
  },
)

// WS connection state toasts & offline UI state
watch(
  () => ws.getSession(sessionId.value)?.connected,
  (now, prev) => {
    if (prev === true && now === false) {
      notifyEvent('Disconnected, trying to reconnect…', 'warning', { verboseOnly: true })
    } else if (prev === false && now === true) {
      notifyEvent('Reconnected', 'success', { verboseOnly: true })
    }
  },
)

const isFailStatus = computed(() => state.value?.status === 'fail')
const isFailTimeout = computed(() => isFailStatus.value && state.value?.fail_reason === 'timeout')
const isFailInvalid = computed(() => isFailStatus.value && state.value?.fail_reason === 'invalid')
const failReasonClass = computed(() => {
  if (isFailTimeout.value) return 'fail-timeout'
  if (isFailInvalid.value) return 'fail-invalid'
  return ''
})

// Add toast for fail reason differences
watch(
  () => state.value?.status,
  (newStatus, oldStatus) => {
    if (newStatus === 'fail' && oldStatus !== 'fail') {
      if (state.value?.fail_reason === 'timeout') {
        notifyEvent('💥 Target missed the window!', 'error', {
          requireInteraction: true,
          autoClose: 0,
        })
      } else {
        notifyEvent('🚫 Invalid', 'error', {
          body: 'Came when not allowed',
          requireInteraction: true,
          autoClose: 0,
        })
      }
    }
  },
)

// Transition hooks for smoother height animation
function setTransitionStyles(el: HTMLElement) {
  el.style.transition =
    'height 220ms cubic-bezier(.2,.8,.2,1), opacity 180ms ease, padding 180ms ease'
  el.style.overflow = 'hidden'
}

const onEnter = (el: Element, done?: () => void) => {
  if (!(el instanceof HTMLElement)) {
    if (done) done()
    return
  }
  // start from zero height
  el.style.opacity = '0'
  const initialPaddingTop = el.style.paddingTop
  const initialPaddingBottom = el.style.paddingBottom
  el.style.paddingTop = '0'
  el.style.paddingBottom = '0'
  el.style.height = '0px'
  setTransitionStyles(el)
  // measure target height after next tick
  nextTick(() => {
    const targetHeight = el.scrollHeight
    requestAnimationFrame(() => {
      el.style.height = targetHeight + 'px'
      el.style.opacity = '1'
      el.style.paddingTop = initialPaddingTop || ''
      el.style.paddingBottom = initialPaddingBottom || ''
    })
  })
  // cleanup after transition
  const totalMs = 260
  setTimeout(() => {
    el.style.height = ''
    el.style.transition = ''
    el.style.overflow = ''
    if (done) done()
  }, totalMs)
}

const onLeave = (el: Element, done?: () => void) => {
  if (!(el instanceof HTMLElement)) {
    if (done) done()
    return
  }
  // start from current height
  const currentHeight = el.scrollHeight
  el.style.height = currentHeight + 'px'
  el.style.opacity = '1'
  setTransitionStyles(el)
  // force reflow then collapse
  requestAnimationFrame(() => {
    el.style.height = '0px'
    el.style.opacity = '0'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
  })
  const totalMs = 260
  setTimeout(() => {
    el.style.height = ''
    el.style.transition = ''
    el.style.overflow = ''
    if (done) done()
  }, totalMs)
}
</script>

<style lang="scss" scoped>
.control-page {
  max-width: 640px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  .countdown {
    font-size: 2rem;
    color: #e67e22;
    font-variant-numeric: tabular-nums;
  }
  .success {
    color: #27ae60;
    font-weight: bold;
  }
  .fail {
    color: #c0392b;
    font-weight: bold;
  }
  .actions {
    margin-top: 1.5rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  input {
    margin-top: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background: var(--color-background-soft);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }
  button {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);
    padding: 0.4rem 0.8rem;
    border-radius: 0.5rem;
    cursor: pointer;
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  .controllers {
    margin-top: 1.5rem;
  }

  .self-control-warning {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #fef3e2;
    border: 1px solid #f59e0b;
    border-radius: 0.5rem;
    color: #92400e;

    h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      font-weight: bold;
    }

    p {
      margin: 0;
      font-size: 0.875rem;
    }

    a {
      color: #d97706;
      text-decoration: underline;
      &:hover {
        color: #92400e;
      }
    }

    // Dark mode styles
    @media (prefers-color-scheme: dark) {
      background: rgba(245, 158, 11, 0.1);
      border-color: rgba(245, 158, 11, 0.3);
      color: #fbbf24;

      a {
        color: #fbbf24;
        &:hover {
          color: #f59e0b;
        }
      }
    }
  }
  .owner-status {
    margin-bottom: 1.5rem;
    .owner-card {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      background: var(--color-background-soft);
      &.offline {
        opacity: 0.6;
      }
      img,
      .placeholder-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
      }
      .placeholder-avatar {
        background: var(--color-border);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text);
        font-weight: bold;
        opacity: 0.6;
      }
      .owner-info {
        display: flex;
        flex-direction: column;
        .name {
          font-weight: bold;
          margin-bottom: 0.25rem;
        }
        .status {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }
    }
  }
  .section-group {
    margin-bottom: 1.5rem;
  }
  .avatars {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.75rem;
  }
  .avatar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    background: var(--color-background-soft);
    position: relative;
  }
  .avatar img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }
  .error {
    color: #c0392b;
    margin-top: 1rem;
  }
  .debug-panel {
    margin-top: 1.5rem;
  }
  .debug-panel .log {
    max-height: 200px;
    overflow: auto;
    background: var(--color-background-soft);
    border: 1px solid var(--color-border);
    padding: 0.5rem;
    border-radius: 0.5rem;
  }

  .join-panel {
    margin: 1rem 0 1.5rem;
    padding: 1rem;
    border: 1px dashed var(--color-border);
    border-radius: 0.6rem;
    background: var(--color-background-soft);
    cursor: default;
    h2 {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .triangle {
      width: 0;
      height: 0;
      border-left: 8px solid currentColor; // ▶ triangle
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
      transform: rotate(0deg);
      transform-origin: center;
    }
    .triangle.open {
      transform: rotate(90deg); // ▼
    }
    .saved {
      color: #22c55e;
    }
    .danger {
      border-color: #ef4444;
      color: #ef4444;
    }

    &.collapsed {
      cursor: pointer;
      padding: 0.75rem 1rem;
      // visually compact when collapsed
      h2 {
        margin: 0;
      }
    }

    // The content area is animated via Vue <transition> using max-height + opacity.
    .join-content {
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
      border-top: 1px dashed rgba(0, 0, 0, 0.04);
      overflow: hidden;

      .presence-toggle {
        margin-top: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        label {
          font-weight: 600;
        }
      }
    }

    // Transition classes generated by the <transition name="join-content"> wrapper
    .join-content-enter-active,
    .join-content-leave-active {
      transition:
        max-height 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
        opacity 180ms ease,
        padding 180ms ease;
    }
    .join-content-enter-from,
    .join-content-leave-to {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
    }
    .join-content-enter-to,
    .join-content-leave-from {
      max-height: 600px; // sufficiently large to fit content
      opacity: 1;
    }
  }
}
</style>
